// Hack for iPhone unexpected vh unit management
import vhCheck from 'vh-check'
import * as Sentry from '@sentry/react'
import { ReactElement } from 'react'
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom'
import { Pages } from 'models/Pages'
import { SurveyStep } from 'models/Survey/SurveyStep'
import AcknowledgeUser from 'components/Survey/AcknowledgeUser/AcknowledgeUser'
import Answer from 'components/Survey/Answer/Answer'
import ConsentPage from 'components/Consent/ConsentPage'
import EmailPage from 'components/Survey/Email/EmailPage'
import HomePage from 'components/Home/HomePage'
import EditionsPage from 'components/Editions/EditionsPage'
import MarkdownPage from 'components/MarkdownPage/MarkdownPage'
import RectifyPrediction from 'components/Survey/RectifyPrediction/RectifyPrediction'
import ResultPage from 'components/Survey/Result/ResultPage'
import WithConsent from 'hoc/WithConsent'
import { ProbabilityPage } from 'components/Survey/Probability/Probability'
import CitizenForecasting from 'components/Survey/CitizenForecasting/CitizenForecasting'
import WithSurvey from 'hoc/WithSurvey'
import { AppLayout } from './layouts/AppLayout'
import { isDesktop } from 'utils/platform'
import SurveyLayout from '@components/Survey/SurveyLayout'
import Prediction from '@components/Survey/Prediction/Prediction'
import Sandbox from '@components/Survey/Sandbox/Sandbox'
import { ProvideShare } from '@hooks/useShare'

const router = () => {
  const needToRedefine = isDesktop
    ? {}
    : {
        bind: false,
        redefineVh: true,
      }

  vhCheck({
    cssVarName: 'vh-offset',
    force: true,
    ...needToRedefine,
  })

  const markdownPages = [
    Pages.MarkdownDemo,
    Pages.About,
    Pages.Methodology,
    Pages.Partners,
    Pages.Contact,
    Pages.Terms,
    Pages.Privacy,
  ] // Every page that will only serve Markdown are defined here

  const basename = __PUBLIC_URL__ ? new URL(__PUBLIC_URL__).pathname : '/'

  return createBrowserRouter(
    createRoutesFromElements(
      <Route element={<AppLayout />}>
        <Route path={`/${Pages.Home}`} element={<HomePage />} />
        <Route path={`/${Pages.Editions}`} element={<EditionsPage />} />
        <Route path={`/${Pages.Consent}`} element={<ConsentPage />} />

        {markdownPages.map((page) => (
          <Route key={page} path={`/${page}`} element={<MarkdownPage contentSlug={page} />} />
        ))}

        <Route
          path={`/${Pages.Survey}`}
          element={
            <ProvideShare>
              <SurveyLayout />
            </ProvideShare>
          }>
          <Route path={SurveyStep.Prediction} element={<Prediction />} />
          <Route path={SurveyStep.Acknowledge} element={<AcknowledgeUser />} />
          <Route path={SurveyStep.Answer} element={<Answer />} />
          <Route path={SurveyStep.Email} element={<EmailPage />} />
          <Route path={SurveyStep.Rectify} element={<RectifyPrediction />} />
          <Route path={SurveyStep.Forecasting} element={<CitizenForecasting />} />
          <Route path={SurveyStep.Results} element={<ResultPage />} />
          <Route path={SurveyStep.Probability} element={<ProbabilityPage />} />
          <Route path={SurveyStep.Sandbox} element={<Sandbox />} />
        </Route>

        <Route path="*" element={<HomePage />} />
      </Route>,
    ),
    {
      basename,
    },
  )
}

export const App = (): ReactElement => <RouterProvider router={router()} />

export default Sentry.withErrorBoundary(WithConsent(WithSurvey(App)), {
  onMount: () => {},
})
